import React, { useRef } from "react";
import PropTypes from "prop-types";
import "./Stepper.css";

function isNavigable(step, onStepClick) {
  if (!onStepClick) return false;
  return step.navigable ?? step.state !== "upcoming";
}

const STATE_TEXT = {
  complete: "completed",
  current: "current step",
  upcoming: "not started",
  error: "has errors",
};

function StatusMarker({ state }) {
  if (state === "complete") {
    return (
      <svg className="od-stepper__check" width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
        <circle cx="10" cy="10" r="8.25" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <path d="M6.5 10.2l2.2 2.2 4.8-4.8" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  return <span className={`od-stepper__dot od-stepper__dot--${state}`} aria-hidden="true" />;
}

StatusMarker.propTypes = { state: PropTypes.string.isRequired };

function Leading({ step, index }) {
  if (step.icon) {
    const Icon = step.icon;
    return <Icon className="od-stepper__icon" width={20} height={20} aria-hidden="true" />;
  }
  return (
    <span className="od-stepper__pip" aria-hidden="true">
      {step.state === "complete" ? "✓" : index + 1}
    </span>
  );
}

Leading.propTypes = { step: PropTypes.object.isRequired, index: PropTypes.number.isRequired };

function StepContent({ step, index }) {
  return (
    <>
      <span className="od-stepper__leading">
        <Leading step={step} index={index} />
      </span>
      <span className="od-stepper__label">{step.label}</span>
      <span className="od-stepper__sr">{STATE_TEXT[step.state]}</span>
      <span className="od-stepper__status">
        <StatusMarker state={step.state} />
      </span>
    </>
  );
}

StepContent.propTypes = { step: PropTypes.object.isRequired, index: PropTypes.number.isRequired };

function Stepper({ steps, orientation = "horizontal", onStepClick = null, size = "md", variant = "default", label = "Steps" }) {
  const buttonRefs = useRef([]);

  const focusStep = (index) => {
    const btn = buttonRefs.current[index];
    if (btn) btn.focus();
  };

  const handleKeyDown = (event, index) => {
    const forward = orientation === "horizontal" ? "ArrowRight" : "ArrowDown";
    const backward = orientation === "horizontal" ? "ArrowLeft" : "ArrowUp";
    if (event.key !== forward && event.key !== backward) return;
    event.preventDefault();
    const dir = event.key === forward ? 1 : -1;
    for (let i = index + dir; i >= 0 && i < steps.length; i += dir) {
      if (isNavigable(steps[i], onStepClick)) {
        focusStep(i);
        return;
      }
    }
  };

  return (
    <ol className={`od-stepper od-stepper--${orientation} od-stepper--${size} od-stepper--${variant}`} aria-label={label}>
      {steps.map((step, index) => {
        const navigable = isNavigable(step, onStepClick);
        return (
          <li
            key={step.key}
            className={`od-stepper__item od-stepper__item--${step.state}`}
            aria-current={step.state === "current" ? "step" : undefined}
          >
            {navigable ? (
              <button
                type="button"
                ref={(el) => {
                  buttonRefs.current[index] = el;
                }}
                className="od-stepper__button od-stepper__button--navigable"
                onClick={() => onStepClick(step.key)}
                onKeyDown={(e) => handleKeyDown(e, index)}
              >
                <StepContent step={step} index={index} />
              </button>
            ) : (
              <span className="od-stepper__button">
                <StepContent step={step} index={index} />
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
}

Stepper.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      state: PropTypes.oneOf(["complete", "current", "upcoming", "error"]).isRequired,
      icon: PropTypes.elementType,
      navigable: PropTypes.bool,
    })
  ).isRequired,
  orientation: PropTypes.oneOf(["horizontal", "vertical"]),
  onStepClick: PropTypes.func,
  size: PropTypes.oneOf(["sm", "md"]),
  variant: PropTypes.oneOf(["default", "progress"]),
  label: PropTypes.string,
};

export default Stepper;
